import React, { useState, useEffect } from 'react';
import { Search, Check, X, Download, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaLicenciasEstudiantes() {
    const [licencias, setLicencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedLicencia, setSelectedLicencia] = useState(null);
    const [razonRechazo, setRazonRechazo] = useState('');
    const [selectedGestion, setSelectedGestion] = useState(new Date().getFullYear());
    const [docentesList, setDocentesList] = useState([]);
    const [loadingDocentes, setLoadingDocentes] = useState(false);

    useEffect(() => {
        loadLicencias();
    }, [selectedGestion]);

    const loadLicencias = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/licencias/estudiantes?gestion=${selectedGestion}`);
            const data = await response.json();
            setLicencias(data.licencias || []);
        } catch (error) {
            console.error('Error:', error);
            setLicencias([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAprobar = async (id) => {
        try {
            await fetch(`${API_URL}/jefatura/licencias/estudiantes/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: 'aprobado' })
            });
            loadLicencias();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleObservar = (licencia) => {
        setSelectedLicencia(licencia);
        setRazonRechazo('');
        setShowModal(true);
    };

    const handleView = async (licencia) => {
        setSelectedLicencia(licencia);
        setShowViewModal(true);
        setDocentesList([]);
        setLoadingDocentes(true);
        try {
            const response = await fetch(`${API_URL}/jefatura/estudiantes/${licencia.id_estudiante}/docentes?gestion=${selectedGestion}`);
            const data = await response.json();
            setDocentesList(data.docentes || []);
        } catch (error) {
            console.error('Error al cargar docentes:', error);
            setDocentesList([licencia.docente]); // Fallback al docente de la licencia
        } finally {
            setLoadingDocentes(false);
        }
    };

    const handleGuardarRechazo = async () => {
        if (!razonRechazo.trim()) {
            alert('Por favor ingrese el motivo del rechazo');
            return;
        }
        try {
            await fetch(`${API_URL}/jefatura/licencias/estudiantes/${selectedLicencia.id_licencia}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: 'observado', razon_rechazo: razonRechazo })
            });
            setShowModal(false);
            setSelectedLicencia(null);
            setRazonRechazo('');
            loadLicencias();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const getEstadoDisplay = (estado) => {
        switch (estado) {
            case 'pendiente': return { text: 'Pendiente', color: 'bg-gray-200 text-gray-700' };
            case 'aprobado': return { text: 'Aprobado', color: 'bg-green-200 text-green-800' };
            case 'observado': return { text: 'Observado', color: 'bg-red-200 text-red-800' };
            default: return { text: 'Pendiente', color: 'bg-gray-200 text-gray-700' };
        }
    };

    const getIconColors = (estado) => {
        switch (estado) {
            case 'aprobado': return { check: 'bg-yellow-500 text-white', x: 'bg-gray-300 text-gray-500' };
            case 'observado': return { check: 'bg-gray-300 text-gray-500', x: 'bg-red-500 text-white' };
            default: return { check: 'bg-gray-300 text-gray-500', x: 'bg-gray-300 text-gray-500' };
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const exportToExcel = () => {
        const headers = ['No', 'Código', 'Nombres', 'Apellidos', 'Cédula de Identidad', 'Carrera', 'Asignatura', 'Docente', 'Tipo Solicitud', 'Motivo', 'Fecha Desde', 'Fecha Hasta', 'Comentarios', 'Estado', 'Observaciones Jefatura'];
        const rows = filteredLicencias.map((l, index) => [
            index + 1,
            l.codigo_estudiante || '',
            l.nombres || '',
            l.apellidos || '',
            l.cedula || '',
            l.carrera || '',
            l.asignatura || '',
            l.docentes_completos || l.docente || '',
            l.tipo_solicitud || '',
            l.motivo_solicitud || '',
            formatDate(l.fecha_desde || l.fecha_licencia) || '',
            formatDate(l.fecha_hasta) || '',
            l.comentarios || '',
            l.estado || 'pendiente',
            l.razon_rechazo || ''
        ]);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

        // Ajustar anchos
        const wscols = [
            { wch: 5 },  // No
            { wch: 12 }, // Código
            { wch: 20 }, // Nombres
            { wch: 20 }, // Apellidos
            { wch: 15 }, // CI
            { wch: 25 }, // Carrera
            { wch: 25 }, // Asignatura
            { wch: 25 }, // Docente
            { wch: 15 }, // Tipo
            { wch: 25 }, // Motivo
            { wch: 12 }, // Desde
            { wch: 12 }, // Hasta
            { wch: 30 }, // Comentarios
            { wch: 12 }, // Estado
            { wch: 30 }  // Obs
        ];
        ws['!cols'] = wscols;

        // Filtros
        ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }) };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Licencias");
        XLSX.writeFile(wb, `licencias_estudiantes_${selectedGestion}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const filteredLicencias = licencias.filter(l =>
        searchTerm === '' ||
        `${l.nombres || ''} ${l.apellidos || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (l.cedula || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (l.codigo_estudiante || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-4 md:p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Licencias</h1>
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={selectedGestion}
                            onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {[2024, 2025, 2026, 2027].map(g => (
                                <option key={g} value={g}>Gestión {g}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                            <Download size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros */}
            {/* <div className="mb-4 flex flex-wrap gap-3 items-center">
                <select
                    value={selectedGestion}
                    onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm"
                >
                    {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                </select>
                <div className="flex-1 max-w-md relative">
                    <input type="text" placeholder="Buscar..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full text-sm" />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                </div>
                <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                    <Download size={20} />
                </button>
            </div> */}

            {/* Tabla Simplificada */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nombres</th>
                                <th className="px-3 py-3 text-center font-semibold border">Apellidos</th>
                                <th className="px-3 py-3 text-center font-semibold border">CI</th>
                                <th className="px-3 py-3 text-left font-semibold w-1/4">Carrera</th>
                                <th className="px-3 py-3 text-center font-semibold border">Desde</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hasta</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Observaciones Jefatura</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredLicencias.length === 0 ? (
                                <tr><td colSpan="10" className="px-4 py-8 text-center text-gray-500">No hay licencias registradas</td></tr>
                            ) : (
                                filteredLicencias.map((l, index) => {
                                    const estadoInfo = getEstadoDisplay(l.estado);
                                    const iconColors = getIconColors(l.estado);
                                    return (
                                        <tr key={l.id_licencia} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-center border">{index + 1}</td>
                                            <td className="px-3 py-2 text-center border font-medium">{l.codigo_estudiante || '-'}</td>
                                            <td className="px-3 py-2 border">{l.nombres || '-'}</td>
                                            <td className="px-3 py-2 border">{l.apellidos || '-'}</td>
                                            <td className="px-3 py-2 border">{l.cedula || '-'}</td>
                                            <td className="px-3 py-2 border font-medium text-blue-900">{l.carrera || '-'}</td>

                                            <td className="px-3 py-2 border text-center whitespace-nowrap">{formatDate(l.fecha_desde || l.fecha_licencia)}</td>
                                            <td className="px-3 py-2 border text-center whitespace-nowrap">{formatDate(l.fecha_hasta) || '-'}</td>
                                            <td className="px-3 py-2 border">
                                                <div className="flex justify-center gap-1">
                                                    <button
                                                        onClick={() => handleView(l)}
                                                        className="p-1.5 rounded-full bg-green-600 text-white hover:bg-green-700"
                                                        title="Ver detalles"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleAprobar(l.id_licencia)}
                                                        className={`p-1.5 rounded-full ${iconColors.check}`}
                                                        title="Aprobar"
                                                        disabled={l.estado === 'aprobado'}
                                                    >
                                                        <Check size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleObservar(l)}
                                                        className={`p-1.5 rounded-full ${iconColors.x}`}
                                                        title="Observar/Rechazar"
                                                        disabled={l.estado === 'observado'}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 border text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${estadoInfo.color}`}>
                                                    {estadoInfo.text}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-sm text-gray-600 max-w-[200px] truncate" title={l.razon_rechazo || ''}>
                                                {l.razon_rechazo || '-'}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Ver Detalles */}
            {showViewModal && selectedLicencia && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
                            <h2 className="text-xl font-bold text-blue-900">Detalles de la Licencia</h2>
                            <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-500">Código</label>
                                    <p className="font-medium">{selectedLicencia.codigo_estudiante || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Nombres</label>
                                    <p className="font-medium">{selectedLicencia.nombres || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Apellidos</label>
                                    <p className="font-medium">{selectedLicencia.apellidos || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Cédula de Identidad</label>
                                    <p className="font-medium">{selectedLicencia.cedula || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Carrera</label>
                                    <p className="font-medium">{selectedLicencia.carrera || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Mención</label>
                                    <p className="font-medium">{selectedLicencia.mencion || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Asignatura</label>
                                    <p className="font-medium">{selectedLicencia.asignatura || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Docentes</label>
                                    <div className="font-medium">
                                        {loadingDocentes ? (
                                            <span className="text-gray-400 animate-pulse italic">Cargando docentes...</span>
                                        ) : docentesList.length > 0 ? (
                                            <ul className="list-disc list-inside">
                                                {docentesList.map((d, i) => (
                                                    <li key={i}>{d}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p>{selectedLicencia.docente || '-'}</p>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Tipo Solicitud</label>
                                    <p className="font-medium">{selectedLicencia.tipo_solicitud || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Motivo</label>
                                    <p className="font-medium">{selectedLicencia.motivo_solicitud || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Desde</label>
                                    <p className="font-medium">{formatDate(selectedLicencia.fecha_desde || selectedLicencia.fecha_licencia)}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Hasta</label>
                                    <p className="font-medium">{formatDate(selectedLicencia.fecha_hasta) || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-500">Estado</label>
                                    <p className="font-medium">
                                        <span className={`px-2 py-1 rounded-full text-xs ${getEstadoDisplay(selectedLicencia.estado).color}`}>
                                            {getEstadoDisplay(selectedLicencia.estado).text}
                                        </span>
                                    </p>
                                </div>
                                <div className="col-span-2 md:col-span-3">
                                    <label className="block text-sm text-gray-500">Comentarios</label>
                                    <p className="font-medium">{selectedLicencia.comentarios || '-'}</p>
                                </div>
                                <div className="col-span-2 md:col-span-3">
                                    <label className="block text-sm text-gray-500">Observaciones Jefatura de Carrera</label>
                                    <p className="font-medium">{selectedLicencia.razon_rechazo || '-'}</p>
                                </div>
                                {/* Archivo Respaldo */}
                                <div className="col-span-2 md:col-span-3">
                                    <label className="block text-sm text-gray-500 mb-2">Archivo Respaldo</label>
                                    {selectedLicencia.respaldo ? (
                                        <div className="flex items-center gap-3">
                                            <a
                                                href={`${API_URL.replace('/api/v1', '')}/${selectedLicencia.respaldo}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                                            >
                                                <Download size={16} />
                                                Ver/Descargar Archivo
                                            </a>
                                            <span className="text-sm text-gray-500">{selectedLicencia.respaldo.split('/').pop()}</span>
                                        </div>
                                    ) : (
                                        <p className="text-gray-400 italic">No se adjuntó archivo</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button onClick={() => setShowViewModal(false)} className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para introducir motivo de rechazo */}
            {showModal && selectedLicencia && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">Licencias</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-700 mb-4">Introduzca el motivo del rechazo de la solicitud de licencia.</p>
                            <textarea
                                value={razonRechazo}
                                onChange={(e) => setRazonRechazo(e.target.value)}
                                placeholder="Escriba aquí el motivo..."
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button
                                onClick={handleGuardarRechazo}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
